/**
 * One-time-code login as a pure state machine (reducer) plus a tiny store that drives the API.
 *
 *   destination ──REQUEST──▶ (requesting) ──REQUEST_OK──▶ code ──VERIFY──▶ (verifying) ──VERIFY_OK──▶ done
 *        ▲                        │ REQUEST_FAIL                ▲   │ RESEND                 │ VERIFY_FAIL
 *        └──────EDIT_DESTINATION──┴─────────────────────────────┘   └──▶ (requesting) ───────┘ (stay on code)
 *
 * Time is passed into the reducer (`now`) so it stays pure and testable.
 */
import { createStore, type StoreApi } from 'zustand/vanilla';
import { isApiError, NETWORK_ERROR } from '../../api/client';
import { OTP_LENGTH, RATE_LIMIT_COOLDOWN_S, RESEND_COOLDOWN_S } from '../../config';

export type Method = 'phone' | 'email';
export type Step = 'destination' | 'code' | 'done';
export type Busy = 'idle' | 'requesting' | 'verifying';

/** Error codes the UI knows how to explain. */
export type OtpErrorCode =
  | 'empty'
  | 'invalid_destination'
  | 'code_incomplete'
  | 'invalid_code'
  | 'rate_limited'
  | 'network'
  | 'generic';

export interface OtpState {
  step: Step;
  method: Method;
  destination: string;
  code: string;
  busy: Busy;
  error: OtpErrorCode | null;
  /** Epoch ms: "send again" is allowed from this moment. */
  resendAt: number | null;
  /** Epoch ms: after a 429 nothing is sent until this moment. */
  blockedUntil: number | null;
  /** Shown once after a successful resend. */
  notice: 'resent' | null;
}

export type OtpEvent =
  | { type: 'SET_METHOD'; method: Method }
  | { type: 'SET_DESTINATION'; value: string }
  | { type: 'SET_CODE'; value: string }
  | { type: 'REQUEST'; now: number }
  | { type: 'REQUEST_OK'; now: number }
  | { type: 'REQUEST_FAIL'; error: OtpErrorCode; now: number; retryAfter?: number | null }
  | { type: 'VERIFY'; now: number }
  | { type: 'VERIFY_OK' }
  | { type: 'VERIFY_FAIL'; error: OtpErrorCode; now: number; retryAfter?: number | null }
  | { type: 'EDIT_DESTINATION' }
  | { type: 'RESET' };

export const initialOtpState = (method: Method = 'phone'): OtpState => ({
  step: 'destination',
  method,
  destination: '',
  code: '',
  busy: 'idle',
  error: null,
  resendAt: null,
  blockedUntil: null,
  notice: null,
});

/** Keep digits only (handles pasted "123 456", "Code: 123-456", full-width digits). */
export function sanitizeCode(raw: string): string {
  const ascii = raw.replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xff10 + 48));
  return ascii.replace(/\D/g, '').slice(0, OTP_LENGTH);
}

/** What we send to the API: phones lose spaces/brackets/dashes; emails are trimmed. */
export function normalizeDestination(method: Method, raw: string): string {
  const v = raw.trim();
  if (method === 'email') return v.toLowerCase();
  const plus = v.startsWith('+') || v.startsWith('00');
  const digits = v.replace(/\D/g, '').replace(/^00/, '');
  return (plus ? '+' : '') + digits;
}

export function validateDestination(method: Method, raw: string): OtpErrorCode | null {
  const v = raw.trim();
  if (!v) return 'empty';
  if (method === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'invalid_destination';
  const digits = v.replace(/\D/g, '');
  if (/[a-zа-яіїєґ]/i.test(v)) return 'invalid_destination';
  return digits.length >= 9 && digits.length <= 15 ? null : 'invalid_destination';
}

export function isBlocked(s: OtpState, now: number): boolean {
  return s.blockedUntil !== null && now < s.blockedUntil;
}

export function canResend(s: OtpState, now: number): boolean {
  return s.step === 'code' && s.busy === 'idle' && !isBlocked(s, now) && (s.resendAt === null || now >= s.resendAt);
}

/** Seconds until "send again" (or until the rate-limit block ends), for the countdown. */
export function secondsUntilResend(s: OtpState, now: number): number {
  const until = Math.max(s.resendAt ?? 0, s.blockedUntil ?? 0);
  return Math.max(0, Math.ceil((until - now) / 1000));
}

function block(now: number, retryAfter?: number | null): number {
  return now + (retryAfter && retryAfter > 0 ? retryAfter : RATE_LIMIT_COOLDOWN_S) * 1000;
}

export function otpReducer(s: OtpState, e: OtpEvent): OtpState {
  switch (e.type) {
    case 'SET_METHOD':
      if (s.step !== 'destination' || s.busy !== 'idle') return s;
      // the server limits per address: a block for the phone says nothing about the e-mail (the server answers again)
      return { ...s, method: e.method, destination: '', error: null, blockedUntil: e.method === s.method ? s.blockedUntil : null };
    case 'SET_DESTINATION':
      if (s.step !== 'destination') return s;
      return {
        ...s,
        destination: e.value,
        error: null,
        blockedUntil: normalizeDestination(s.method, e.value) === normalizeDestination(s.method, s.destination) ? s.blockedUntil : null,
      };
    case 'SET_CODE':
      if (s.step !== 'code') return s;
      return { ...s, code: sanitizeCode(e.value), error: s.error === 'rate_limited' ? s.error : null, notice: null };

    case 'REQUEST': {
      if (s.busy !== 'idle' || s.step === 'done') return s;
      if (isBlocked(s, e.now)) return { ...s, error: 'rate_limited' };
      if (s.step === 'code') {
        // resend
        if (!canResend(s, e.now)) return s;
        return { ...s, busy: 'requesting', error: null, notice: null };
      }
      const invalid = validateDestination(s.method, s.destination);
      if (invalid) return { ...s, error: invalid };
      return { ...s, busy: 'requesting', error: null };
    }
    case 'REQUEST_OK':
      if (s.busy !== 'requesting') return s;
      return {
        ...s,
        busy: 'idle',
        notice: s.step === 'code' ? 'resent' : null,
        step: 'code',
        code: '',
        error: null,
        resendAt: e.now + RESEND_COOLDOWN_S * 1000,
      };
    case 'REQUEST_FAIL':
      if (s.busy !== 'requesting') return s;
      return {
        ...s,
        busy: 'idle',
        error: e.error,
        blockedUntil: e.error === 'rate_limited' ? block(e.now, e.retryAfter) : s.blockedUntil,
      };

    case 'VERIFY':
      if (s.step !== 'code' || s.busy !== 'idle') return s;
      if (isBlocked(s, e.now)) return { ...s, error: 'rate_limited' };
      if (s.code.length !== OTP_LENGTH) return { ...s, error: 'code_incomplete' };
      return { ...s, busy: 'verifying', error: null, notice: null };
    case 'VERIFY_OK':
      if (s.busy !== 'verifying') return s;
      return { ...s, busy: 'idle', step: 'done', error: null };
    case 'VERIFY_FAIL':
      if (s.busy !== 'verifying') return s;
      return {
        ...s,
        busy: 'idle',
        error: e.error,
        // a wrong code is cleared so the person can simply type again
        code: e.error === 'invalid_code' ? '' : s.code,
        blockedUntil: e.error === 'rate_limited' ? block(e.now, e.retryAfter) : s.blockedUntil,
      };

    case 'EDIT_DESTINATION':
      if (s.busy !== 'idle') return s;
      return { ...s, step: 'destination', code: '', error: null, notice: null, resendAt: null };
    case 'RESET':
      return initialOtpState(s.method);
  }
}

/** Map anything thrown by the API layer to an error code the screen can explain. */
export function toOtpError(e: unknown, phase: 'request' | 'verify'): { error: OtpErrorCode; retryAfter: number | null } {
  if (!isApiError(e)) return { error: 'generic', retryAfter: null };
  const retryAfter = e.retryAfter;
  if (e.status === 429 || e.code === 'rate_limited') return { error: 'rate_limited', retryAfter };
  if (e.code === NETWORK_ERROR) return { error: 'network', retryAfter };
  if (e.code === 'invalid_destination' || (phase === 'request' && e.status === 422)) return { error: 'invalid_destination', retryAfter };
  if (phase === 'verify' && (e.code === 'invalid_code' || e.status === 401 || e.status === 422)) return { error: 'invalid_code', retryAfter };
  return { error: 'generic', retryAfter };
}

export interface OtpApi {
  requestCode: (destination: string) => Promise<unknown>;
  verifyCode: (destination: string, code: string) => Promise<unknown>;
}

export interface OtpStore extends OtpState {
  dispatch: (e: OtpEvent) => void;
  setMethod: (m: Method) => void;
  setDestination: (v: string) => void;
  setCode: (v: string) => void;
  /** Send (or resend) the code. */
  request: () => Promise<void>;
  verify: () => Promise<void>;
  editDestination: () => void;
}

export function createOtpStore(api: OtpApi, opts: { now?: () => number; method?: Method; onSuccess?: () => void } = {}): StoreApi<OtpStore> {
  const now = opts.now ?? Date.now;
  return createStore<OtpStore>((set, get) => {
    const dispatch = (e: OtpEvent) => set((s) => otpReducer(s, e));
    return {
      ...initialOtpState(opts.method),
      dispatch,
      setMethod: (method) => dispatch({ type: 'SET_METHOD', method }),
      setDestination: (value) => dispatch({ type: 'SET_DESTINATION', value }),
      setCode: (value) => dispatch({ type: 'SET_CODE', value }),
      editDestination: () => dispatch({ type: 'EDIT_DESTINATION' }),
      async request() {
        dispatch({ type: 'REQUEST', now: now() });
        const s = get();
        if (s.busy !== 'requesting') return;
        try {
          await api.requestCode(normalizeDestination(s.method, s.destination));
          dispatch({ type: 'REQUEST_OK', now: now() });
        } catch (e) {
          const { error, retryAfter } = toOtpError(e, 'request');
          dispatch({ type: 'REQUEST_FAIL', error, retryAfter, now: now() });
        }
      },
      async verify() {
        dispatch({ type: 'VERIFY', now: now() });
        const s = get();
        if (s.busy !== 'verifying') return;
        try {
          await api.verifyCode(normalizeDestination(s.method, s.destination), s.code);
          dispatch({ type: 'VERIFY_OK' });
          opts.onSuccess?.();
        } catch (e) {
          const { error, retryAfter } = toOtpError(e, 'verify');
          dispatch({ type: 'VERIFY_FAIL', error, retryAfter, now: now() });
        }
      },
    };
  });
}
